import React, {
  createContext, useCallback, useContext, useLayoutEffect, useMemo, useState,
} from "react"
import { Box, IconButton } from "@chakra-ui/react"
import SimpleBar from "simplebar-react"
import Icon from "components/elements/Icon"

type Side = "left" | "right"

type LayoutContextValue = {
  leftPanelOpen: boolean
  toggleLeftPanel: () => void
  panelWidths: Record<Side, number>
  setPanelWidth: (side: Side, width: number) => void
}

const LayoutContext = createContext<LayoutContextValue | null>(null)

/** The left panel's open state, for a toggle inside or outside it. */
export const useEditorLayout = () => {
  const context = useContext(LayoutContext)
  if (!context) throw new Error("useEditorLayout must be used inside EditorContent")

  return context
}

type ContentProps = {
  children: React.ReactNode
}

/** The editor's working area: the canvas fills it; panels and the toolbar float above it. */
export const EditorContent = ({ children }: ContentProps) => {
  const [leftPanelOpen, setLeftPanelOpen] = useState(true)
  const [panelWidths, setPanelWidths] = useState<Record<Side, number>>({ left: 0, right: 0 })

  const toggleLeftPanel = useCallback(() => setLeftPanelOpen((open) => !open), [])
  const setPanelWidth = useCallback((side: Side, width: number) => {
    setPanelWidths((widths) => (widths[side] === width ? widths : { ...widths, [side]: width }))
  }, [])

  const value = useMemo(() => ({
    leftPanelOpen, toggleLeftPanel, panelWidths, setPanelWidth,
  }), [leftPanelOpen, toggleLeftPanel, panelWidths, setPanelWidth])

  return (
    <LayoutContext.Provider value={value}>
      <Box position="relative" h="full" overflow="hidden" bg="white">
        {children}
      </Box>
    </LayoutContext.Provider>
  )
}

type PanelProps = {
  side: Side
  width: number
  children: React.ReactNode
}

/**
 * A side panel over the canvas. The left one slides out with the editor's panel toggle;
 * while it's closed, a button brings it back.
 */
export const EditorPanel = ({ side, width, children }: PanelProps) => {
  const { leftPanelOpen, toggleLeftPanel, setPanelWidth } = useEditorLayout()
  const open = side === "right" || leftPanelOpen

  // Before the first paint, so the toolbar is centred from the start
  useLayoutEffect(() => {
    setPanelWidth(side, width)
  }, [side, width, setPanelWidth])

  return (
    <>
      <Box
        as={SimpleBar}
        autoHide
        position="absolute"
        top={0}
        bottom={0}
        {...(side === "left"
          ? {
            left: open ? 0 : "-350px",
            borderRightWidth: 1,
            transition: "left 0.3s cubic-bezier(.215, .61, .355, 1)",
            willChange: "transform",
          }
          : { right: 0, borderLeftWidth: 1 })}
        width={`${width}px`}
        bgColor="gray.100"
        shadow="sm"
        zIndex={10}
      >
        {children}
      </Box>

      {!open && (
        <IconButton
          aria-label="Open left panel"
          icon={<Icon icon="next-arrow" />}
          variant="outline"
          onClick={toggleLeftPanel}
          position="absolute"
          top={4}
          left={4}
          zIndex={20}
          size="sm"
          bgColor="white"
          borderWidth={1}
          borderColor="gray.300"
          boxShadow="md"
          _hover={{
            bgColor: "gray.50",
          }}
        />
      )}
    </>
  )
}
